import React, { useMemo, useState } from 'react';

import { Box, Flex, Icon, icons } from '@tapresearch/ui/components';
import { useDisclosure } from '@tapresearch/ui/hooks';
import { useTapSurveyQuestionsList } from '../../../api';
import { FilterProvider, FilterValue, OpenFilterButton } from '../../common';
import {
  useGetFilterFromLocation,
  useUpdateLocationFromFilter,
  useCurrentUser,
} from '../../hook';
import useAsset from '../../hook/useAsset';
import { ResultsFilterBadges } from './ResultsFilterBadges';
import { ResultsFilterDrawer } from './ResultsFilterDrawer';
import { SurveyQuestionItemResults } from './SurveyQuestionItemResults';

export type DemographicQuestionDetails = {
  type?: string;
  question_id?: number;
  name?: string;
  answer_type?: number;
};

export type ComparableTapQuestionDetails = {
  type?: string;
  answer_type?: number;
  answers?: Record<string, string>[];
  name?: string;
  total_respondents?: number;
};

interface ResultsFilter {
  filter: Record<string, string[]>;
}

interface SurveyResultsWrapperProps {
  tapSurveyIdentifier: string;
  countryId: string;
  languageId: string;
  questionsHash: Record<string, unknown>;
  campaignResultIdentifier?: string;
}

export const SurveyResultsWrapper = ({
  tapSurveyIdentifier,
  countryId,
  languageId,
  questionsHash,
  campaignResultIdentifier,
}: SurveyResultsWrapperProps) => {
  const asset = useAsset();
  const { user: currentUser } = useCurrentUser();

  const {
    onOpen: onOpenFilterDrawer,
    onClose: onCloseFilterDrawer,
    isOpen: isOpenFilterDrawer,
  } = useDisclosure();

  const defaultFilter = useGetFilterFromLocation<ResultsFilter>({
    filter: {},
  });

  const [resultsFilter, setResultsFilter] = useState<ResultsFilter>(
    defaultFilter as ResultsFilter
  );

  const updateFilter = (
    filterValues: Record<string, FilterValue | FilterValue[]>
  ) => {
    setResultsFilter({
      ...resultsFilter,
      filter: filterValues as unknown as Record<string, string[]>,
    });
  };

  const { data: tapQuestions = [] } = useTapSurveyQuestionsList(
    tapSurveyIdentifier,
    campaignResultIdentifier
  );

  useUpdateLocationFromFilter(resultsFilter);

  const [demographicCompareQuestions, tapCompareQuestions] = useMemo(() => {
    return [
      Object.entries(questionsHash)
        .filter(([questionId]) => !questionId.startsWith('tq_'))
        .reduce<Record<string, DemographicQuestionDetails>>(
          (acc, [questionId, question]) => {
            return { ...acc, [questionId]: question };
          },
          {}
        ),
      Object.entries(questionsHash)
        .filter(([questionId]) => !!questionId.startsWith('tq_'))
        .filter(
          ([, question]: [undefined, ComparableTapQuestionDetails]) =>
            ![
              asset.constants.TAP_ANSWER_TYPE_RANKING,
              asset.constants.TAP_ANSWER_TYPE_MESSAGE,
              asset.constants.TAP_ANSWER_TYPE_FREE_TEXT,
            ].includes(question.answer_type)
        )
        .reduce<Record<string, ComparableTapQuestionDetails>>(
          (acc, [questionId, question]) => {
            return { ...acc, [questionId]: question };
          },
          {}
        ),
    ];
  }, [asset, questionsHash]);

  return (
    <Box data-testid="survey-results-wrapper">
      <FilterProvider
        defaultFilterValues={defaultFilter['filter']}
        onFilterUpdated={updateFilter}
      >
        {currentUser && (
          <Box>
            <ResultsFilterBadges onTagClicked={onOpenFilterDrawer} />
            <Flex>
              <OpenFilterButton
                data-testid="survey-results-open-filters-button"
                onClick={onOpenFilterDrawer}
                omitFilters={['page', 'query', 'sort_by', 'status']}
              >
                <Icon mr="2" icon={icons.fi.Filter} /> Filter
              </OpenFilterButton>
            </Flex>
          </Box>
        )}
        <Box mt="5">
          {tapQuestions.map((tapQuestion) => (
            <SurveyQuestionItemResults
              key={tapQuestion.id}
              tapQuestion={tapQuestion.attributes}
              countryId={countryId}
              languageId={languageId}
              filters={resultsFilter.filter}
              campaignResultIdentifier={campaignResultIdentifier}
              comparisonQuestions={[
                demographicCompareQuestions,
                tapCompareQuestions,
              ]}
            />
          ))}
        </Box>

        {currentUser && languageId && countryId && (
          <ResultsFilterDrawer
            isOpen={isOpenFilterDrawer}
            onClose={onCloseFilterDrawer}
            countryId={countryId}
            languageId={languageId}
          />
        )}
      </FilterProvider>
    </Box>
  );
};
